import { Level } from "./LevelModel";

export interface AttemptLog {
  timestamp: string;
  action: string;
  data?: any;
}

export interface MultiplierData {
  type: string;
  value: number;
  reason: string;
}

export class UserAttempt {
  id?: number;
  uid: number;
  level: number;
  started: Date | null;
  completed: Date | null;
  duration: number;
  sessions: number;
  moves: number;
  score: number;
  tokensAwarded: number;
  mistakes: string[]; // Track suboptimal moves as detailed strings
  logs: AttemptLog[];
  multipliersApplied: MultiplierData[];
  adultMode: boolean;
  timezoneOffset: number; // Minutes offset from UTC (e.g., -480 for PST)
  timezoneName: string; // IANA timezone name (e.g., "America/Los_Angeles")

  constructor(uid: number, levelId: number, adultMode: boolean) {
    this.uid = uid;
    this.level = levelId;
    this.started = null;
    this.completed = null;
    this.duration = 0;
    this.sessions = 1; // Initialize with 1 session
    this.moves = 0;
    this.score = 0;
    this.tokensAwarded = 0;
    this.mistakes = []; // Initialize mistakes as empty array
    this.logs = [];
    this.multipliersApplied = [];
    this.adultMode = adultMode;

    // Capture timezone information
    this.timezoneOffset = new Date().getTimezoneOffset(); // Minutes offset from UTC
    this.timezoneName = this.getTimezoneName();

    console.debug("Created new UserAttempt:", {
      uid,
      levelId,
      adultMode,
      timezone: this.timezoneName,
      timezoneOffset: this.timezoneOffset,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Start tracking a new attempt
   */
  start(): void {
    if (!this.started) {
      this.started = new Date();
      this.addLog("attempt_started");
      console.debug("Started level attempt:", {
        levelId: this.level,
        startTime: this.started.toISOString(),
      });
    }
  }

  /**
   * Record a move made by the user
   * @param moveType Type of move (e.g., 'transfer', 'adjust_budget')
   * @param data Additional move data
   */
  recordMove(moveType: string, data?: any): void {
    this.moves++;
    this.addLog("move_made", { moveType, ...data });
    console.debug("Recorded move:", {
      moveNumber: this.moves,
      type: moveType,
      data,
    });
  }

  /**
   * Add a multiplier to the attempt
   */
  addMultiplier(type: string, value: number, reason: string): void {
    this.multipliersApplied.push({ type, value, reason });
    console.debug("Added score multiplier:", {
      type,
      value,
      reason,
    });
  }

  /**
   * Add a log entry
   */
  private addLog(action: string, data?: any): void {
    this.logs.push({
      timestamp: new Date().toISOString(),
      action,
      ...(data && { data }),
    });
  }

  /**
   * Complete the attempt
   * @param finalScore The final score achieved
   * @param tokensAwarded Number of tokens awarded
   * @param finalAdultMode Optional final adult mode setting (defaults to current)
   */
  complete(
    finalScore: number,
    tokensAwarded: number,
    finalAdultMode?: boolean,
  ): void {
    if (!this.started) {
      throw new Error("Cannot complete attempt that hasn't started");
    }

    this.completed = new Date();
    this.score = finalScore;
    this.tokensAwarded = tokensAwarded;

    // Update adultMode to final value if provided
    if (finalAdultMode !== undefined) {
      this.adultMode = finalAdultMode;
      console.debug("Updated adultMode to final value:", finalAdultMode);
    }

    // Calculate duration in milliseconds
    this.duration = this.completed.getTime() - this.started.getTime();

    this.addLog("attempt_completed", {
      score: finalScore,
      tokens: tokensAwarded,
      duration: this.duration,
      finalAdultMode: this.adultMode,
    });

    console.debug("Completed level attempt:", {
      levelId: this.level,
      duration: this.duration,
      moves: this.moves,
      score: finalScore,
      tokens: tokensAwarded,
      adultMode: this.adultMode,
    });
  }

  /**
   * Get the IANA timezone name for the user's current timezone
   */
  private getTimezoneName(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn("Unable to determine timezone name, using UTC");
      return "UTC";
    }
  }

  /**
   * Convert the attempt to a format matching the database schema
   */
  toJSON(): any {
    const startedWithTZ = this.started
      ? this.getLocalTimestamp(this.started)
      : null;
    const completedWithTZ = this.completed
      ? this.getLocalTimestamp(this.completed)
      : null;

    return {
      uid: this.uid,
      level: this.level,
      started: startedWithTZ, // Local time with timezone offset: 2025-06-22T23:05:37.697-05:00
      completed: completedWithTZ, // Local time with timezone offset: 2025-06-22T23:05:41.876-05:00
      duration: this.duration,
      sessions: this.sessions,
      moves: this.moves,
      score: this.score,
      tokens_awarded: this.tokensAwarded,
      mistakes: this.mistakes, // Include mistakes in backend data
      logs: this.logs,
      multipliers_applied: this.multipliersApplied,
      adult_mode: this.adultMode,
      tz: this.timezoneName, // IANA timezone identifier (e.g., "America/Chicago")
    };
  }

  /**
   * Convert UTC timestamp to local timestamp string with timezone info embedded
   */
  private getLocalTimestamp(date: Date): string {
    try {
      // Method 1: Use Intl.DateTimeFormat to get timezone-aware string
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: this.timezoneName,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
        hour12: false,
        timeZoneName: "longOffset",
      });

      const parts = formatter.formatToParts(date);
      const partsMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));

      // Build ISO 8601 string with timezone offset: 2025-06-22T23:05:37.697-05:00
      return `${partsMap.year}-${partsMap.month}-${partsMap.day}T${partsMap.hour}:${partsMap.minute}:${partsMap.second}.${partsMap.fractionalSecond}${partsMap.timeZoneName}`;
    } catch (error) {
      // Fallback: Manual timezone offset calculation
      console.warn("Advanced timezone formatting failed, using manual offset");
      return this.getManualTimezoneString(date);
    }
  }

  /**
   * Fallback method to create timezone-aware timestamp manually
   */
  private getManualTimezoneString(date: Date): string {
    // Calculate local time
    const localTime = new Date(
      date.getTime() - this.timezoneOffset * 60 * 1000,
    );

    // Calculate timezone offset string (e.g., "-05:00", "+09:00")
    const offsetHours = Math.floor(Math.abs(this.timezoneOffset) / 60);
    const offsetMinutes = Math.abs(this.timezoneOffset) % 60;
    const offsetSign = this.timezoneOffset <= 0 ? "+" : "-"; // Note: getTimezoneOffset() is inverted
    const offsetString = `${offsetSign}${offsetHours.toString().padStart(2, "0")}:${offsetMinutes.toString().padStart(2, "0")}`;

    // Build ISO 8601 string: 2025-06-22T23:05:37.697-05:00
    const isoString = localTime.toISOString();
    return isoString.replace("Z", offsetString);
  }
}
